import * as React from 'react';
import { cn } from '../../lib/utils';
import { Label } from '../Label';
import {
  floatingFieldLabelClassName,
  floatingFieldLabelInsetClassName,
} from '../Label/helpers';
import type { InputProps } from './types';

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, id, label, labelProps, ...rest }, ref) => {
    const generatedId = React.useId();
    const shouldRenderLabel = label != null && type !== 'file';
    const inputId = id ?? (shouldRenderLabel ? generatedId : undefined);
    const { className: labelClassName, ...restLabelProps } = labelProps ?? {};

    const inputElement = (
      <input
        ref={ref}
        id={inputId}
        type={type}
        data-slot='input-alt'
        className={cn(
          // Layout and sizing
          'eye:h-48 eye:w-full eye:min-w-0 eye:rounded-md eye:border eye:px-16',
          'eye:[[type=file]]:py-0 eye:[[type=file]]:pr-16 eye:[[type=file]]:pl-0 eye:[[type=file]]:leading-48',
          // Typography
          'eye:body-m eye:text-black eye:placeholder:text-grey-placeholder',
          // Surface
          'eye:border-grey-100 eye:bg-grey-5',
          // File input content
          'eye:file:m-0 eye:file:inline-flex eye:file:h-48 eye:file:items-center eye:file:self-stretch eye:file:border-0 eye:file:bg-transparent eye:file:py-0 eye:file:pr-8 eye:file:pl-16 eye:file:text-14 eye:file:leading-22 eye:file:font-medium eye:file:text-black',
          // Interaction states
          'eye:outline-offset-3 eye:outline-black eye:transition-[border-color,outline-color,box-shadow]',
          'eye:hover:border-black',
          'eye:disabled:cursor-not-allowed eye:disabled:border-grey-100 eye:disabled:bg-grey-disabled eye:disabled:text-grey-disabled',
          // Focus state
          'eye:focus:border-black eye:focus:outline-2 eye:focus:outline-black',
          // Validation state
          'eye:aria-invalid:border-red-error eye:aria-invalid:hover:border-red-error eye:aria-invalid:focus:border-red-error',
          className,
        )}
        {...rest}
      />
    );

    if (!shouldRenderLabel) {
      return inputElement;
    }

    return (
      <div
        data-slot='input-with-label'
        className='eye:group/input-with-label eye:relative eye:block eye:w-full'
      >
        <Label
          htmlFor={inputId}
          className={cn(
            floatingFieldLabelClassName,
            floatingFieldLabelInsetClassName,
            'eye:top-1',
            // Disabled states
            'eye:group-has-disabled/input-with-label:text-grey-disabled',
            // Validation states
            'eye:group-has-[[data-slot=input-alt][aria-invalid=true]]/input-with-label:text-red-100',
            labelClassName,
          )}
          {...restLabelProps}
        >
          {label}
        </Label>
        {inputElement}
      </div>
    );
  },
);

Input.displayName = 'Input';

export { Input };
